import React, { useEffect, useState } from 'react';
import Header from './components/Header';
import LatestResults from './components/LatestResults';
import PredictionCard from './components/PredictionCard';
import ActionButtons from './components/ActionButtons';
import StatsDisplay from './components/StatsDisplay';
import HistoryView from './components/HistoryView';
import GeneratorListView from './components/GeneratorListView';
import ShareModal from './components/ShareModal';
import AboutSection from './components/AboutSection';
import { LottoDraw, PredictionSet, PredictionBatch, LottoStats, ViewState, PredictionStrategy } from './types';
import { fetchLatestLottoResults, generateSmartPredictions, fetchLottoStats, fetchHistory, syncNLCBHistory, savePredictionBatch, fetchLatestPredictionBatch, fetchPredictionBatches, fetchLottoStatus, refreshCurrentNLCB } from './services/lottoService';
import { getNextNLCBDraw } from './services/drawSchedule';
import { AlertCircle, LayoutDashboard, History, ListChecks } from 'lucide-react';

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewState>('dashboard');
  
  const [latestDraw, setLatestDraw] = useState<LottoDraw | null>(null);
  const [history, setHistory] = useState<LottoDraw[]>([]);
  const [stats, setStats] = useState<LottoStats | null>(null);
  const [predictions, setPredictions] = useState<PredictionSet[]>([]);
  const [predictionBatches, setPredictionBatches] = useState<PredictionBatch[]>([]);
  const [loadingPredictionBatches, setLoadingPredictionBatches] = useState(false);
  const [lastObservedVerifiedAt, setLastObservedVerifiedAt] = useState<string | null>(null);
  const [predictionStrategy, setPredictionStrategy] = useState<PredictionStrategy>('All Strategies');
  const [wheelMode, setWheelMode] = useState(false);
  
  const [loadingLatest, setLoadingLatest] = useState<boolean>(true);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [loadingStats, setLoadingStats] = useState<boolean>(true);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareInitialSelection, setShareInitialSelection] = useState<Set<string> | null>(null);
  const [liveVerificationWarning, setLiveVerificationWarning] = useState<string | null>(null);

  // Initial Data Fetch
  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const checkScheduledRefresh = async () => {
      try {
        const status = await fetchLottoStatus();
        const verifiedAt = status.officialVerifiedAt || null;
        if (!cancelled && verifiedAt && lastObservedVerifiedAt && verifiedAt !== lastObservedVerifiedAt) {
          await reloadFromLocalArchive();
        }
        if (!cancelled && verifiedAt) setLastObservedVerifiedAt(verifiedAt);
      } catch (err) {
        console.warn('Unable to poll scheduled Lotto refresh status', err);
      }
    };
    void checkScheduledRefresh();
    const timer = window.setInterval(checkScheduledRefresh, 30000);
    return () => { cancelled = true; window.clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastObservedVerifiedAt, currentView]);

  const loadSavedPredictions = async () => {
    try {
      const batch = await fetchLatestPredictionBatch();
      if (batch?.predictions?.length) setPredictions(batch.predictions);
    } catch (err) {
      console.warn('Unable to load saved predictions', err);
    }
  };

  const loadPredictionBatches = async () => {
    setLoadingPredictionBatches(true);
    try {
      setPredictionBatches(await fetchPredictionBatches());
    } catch (err) {
      console.warn('Unable to load generator history', err);
    } finally {
      setLoadingPredictionBatches(false);
    }
  };

  const reloadFromLocalArchive = async () => {
    const [latestResult, historyResult] = await Promise.allSettled([fetchLatestLottoResults(), fetchHistory()]);
    if (latestResult.status === 'fulfilled') setLatestDraw(latestResult.value);
    if (historyResult.status === 'fulfilled') {
      setHistory(historyResult.value);
      setStats(await fetchLottoStats(historyResult.value));
    }
    await loadSavedPredictions();
    if (currentView === 'generator-list') await loadPredictionBatches();
  };

  const loadInitialData = async () => {
    setLoadingLatest(true);
    setLoadingHistory(true);
    setLoadingStats(true);
    setError(null);
    setLiveVerificationWarning(null);
    try {
      await refreshCurrentNLCB();
    } catch (err) {
      console.warn('Unable to verify current NLCB data live', err);
      setLiveVerificationWarning('Live NLCB verification failed. Showing the most recently verified local draw and jackpot data.');
    }

    // Re-assess saved predictions only after the current official draw has been refreshed.
    void loadSavedPredictions();

    const [latestResult, historyResult] = await Promise.allSettled([
      fetchLatestLottoResults(),
      fetchHistory(),
    ]);

    if (latestResult.status === 'fulfilled') {
      setLatestDraw(latestResult.value);
    } else {
      console.error(latestResult.reason);
      setError(latestResult.reason instanceof Error ? latestResult.reason.message : "Unable to retrieve latest results.");
    }
    setLoadingLatest(false);

    if (historyResult.status === 'fulfilled') {
      setHistory(historyResult.value);
      setStats(await fetchLottoStats(historyResult.value));
    } else {
      console.warn("Failed to load history", historyResult.reason);
      setStats(await fetchLottoStats([]));
      if (latestResult.status === 'fulfilled') {
        setError(historyResult.reason instanceof Error ? historyResult.reason.message : "Latest result loaded, but history is unavailable.");
      }
    }
    setLoadingHistory(false);
    setLoadingStats(false);
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const histData = await fetchHistory();
      setHistory(histData);
      setStats(await fetchLottoStats(histData));
      setError(null);
    } catch (err) {
      console.error("Failed to load history", err);
      setError(err instanceof Error ? err.message : "Failed to load history.");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load history when switching to view
  useEffect(() => {
    if (currentView === 'history' && history.length === 0) {
      loadHistory();
    }
  }, [currentView, history.length]);


  useEffect(() => {
    if (currentView === 'generator-list') void loadPredictionBatches();
  }, [currentView]);

  const handleGenerate = async () => {
    if (currentView !== 'dashboard') setCurrentView('dashboard');
    setIsGenerating(true);
    setPredictions([]);
    
    // Scroll to predictions area only on mobile/small screens where they might be out of view
    if (window.innerWidth < 1024) {
      setTimeout(() => {
        document.getElementById('predictions-area')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }

    try {
      // Use history in generation if available
      let predictionHistory = history;
      let predictionStats = stats;
      if (predictionHistory.length === 0) {
        try {
          predictionHistory = await fetchHistory();
          setHistory(predictionHistory);
          predictionStats = await fetchLottoStats(predictionHistory);
          setStats(predictionStats);
        } catch {
          // Prediction generation has a safe random fallback when history is unavailable.
        }
      }
      const newPredictions = await generateSmartPredictions(latestDraw, predictionStats, predictionHistory, predictionStrategy, wheelMode);
      const nextDraw = getNextNLCBDraw();
      const latestKnownDrawNumber = latestDraw?.drawNumber
        ?? predictionHistory.reduce((max, draw) => Math.max(max, draw.drawNumber || 0), 0);
      if (!latestKnownDrawNumber) throw new Error('A current NLCB draw number is required before predictions can be saved. Sync NLCB first.');

      const savedBatch = await savePredictionBatch({
        targetDrawNumber: latestKnownDrawNumber + 1,
        targetDrawDate: nextDraw.date,
        predictions: newPredictions,
      });
      setPredictions(savedBatch.predictions);
      void loadPredictionBatches();
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Failed to generate predictions. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    setError(null);
    try {
      const result = await syncNLCBHistory();
      await loadInitialData();
      await loadSavedPredictions();
      setError(result.cachedDraws < result.targetDraws
        ? `NLCB sync stored ${result.cachedDraws} draws. Run Sync NLCB again if the browser challenge interrupted the initial ${result.targetDraws}-draw seed.`
        : null);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'NLCB browser-assisted sync failed.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleOpenShare = (specificSelection?: Set<string>) => {
    setShareInitialSelection(specificSelection || null);
    setIsShareModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#0f172a] pb-24 font-sans text-slate-100">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-slate-900/80 backdrop-blur-md border-b border-slate-800 z-40 no-print">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="font-bold text-yellow-500 font-['Space_Grotesk']">TT Lotto+</div>
          <div className="flex gap-1">
            <button 
              onClick={() => setCurrentView('dashboard')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${currentView === 'dashboard' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              <LayoutDashboard size={18} />
              Dashboard
            </button>
            <button 
              onClick={() => setCurrentView('history')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${currentView === 'history' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              <History size={18} />
              Results History
            </button>
            <button
              onClick={() => setCurrentView('generator-list')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${currentView === 'generator-list' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              <ListChecks size={18} />
              Generator List
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 pt-24">
        {currentView === 'dashboard' ? (
          <>
            <Header />

            {error && (
              <div className="bg-red-900/30 border border-red-800 text-red-200 p-4 rounded-xl mb-6 flex items-center gap-3 animate-in fade-in slide-in-from-top-4">
                <AlertCircle className="shrink-0" />
                <p>{error}</p>
              </div>
            )}

            {liveVerificationWarning && (
              <div className="bg-amber-950/30 border border-amber-700/60 text-amber-200 p-4 rounded-xl mb-6 flex items-center gap-3">
                <AlertCircle className="shrink-0" />
                <p>{liveVerificationWarning}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              {/* Left Column: Results & Stats (Span 2) */}
              <div className="lg:col-span-2 space-y-6">
                <section aria-label="Latest Results">
                  <LatestResults 
                    draw={latestDraw} 
                    loading={loadingLatest} 
                    onRetry={loadInitialData}
                  />
                </section>

                <section aria-label="Historical Statistics">
                  <StatsDisplay stats={stats} loading={loadingStats} />
                </section>
              </div>

              {/* Right Column: Predictions (Span 1) */}
              <div className="lg:col-span-1">
                <section 
                  id="predictions-area" 
                  aria-label="Generated Predictions"
                  className="space-y-4 sticky top-24"
                >
                  <div className="flex items-center gap-2 mb-4 text-slate-300">
                    <h2 className="text-xl font-bold font-['Space_Grotesk']">Generated Number Sets</h2>
                    <div className="h-px bg-slate-700 flex-grow"></div>
                  </div>

                  <div className="flex flex-col gap-4">
                    {predictions.map((pred, idx) => (
                      <PredictionCard 
                        key={pred.id || idx} 
                        prediction={pred} 
                        index={idx}
                        onShare={() => handleOpenShare(new Set([`pred-${idx}`]))}
                      />
                    ))}
                  </div>

                  {!isGenerating && predictions.length === 0 && !loadingLatest && (
                    <div className="text-center py-12 px-6 bg-slate-800/30 rounded-xl border border-slate-700/50 border-dashed text-slate-500">
                      <p>Tap "Generate Numbers" to populate this area with strategy-based number sets.</p>
                    </div>
                  )}
                </section>
              </div>
            </div>

            <AboutSection />
          </>
        ) : currentView === 'history' ? (
          <section aria-label="Results History">
            <HistoryView 
              history={history} 
              loading={loadingHistory} 
              onRefresh={loadHistory} 
            />
          </section>
        ) : (
          <section aria-label="Generator List">
            <GeneratorListView batches={predictionBatches} loading={loadingPredictionBatches} onRefresh={loadPredictionBatches} />
          </section>
        )}
      </div>

      <ActionButtons 
        onGenerate={handleGenerate}
        onPrint={handlePrint}
        onShare={() => handleOpenShare()}
        onSync={handleSync}
        isGenerating={isGenerating}
        isSyncing={isSyncing}
        hasPredictions={predictions.length > 0}
        strategy={predictionStrategy}
        onStrategyChange={setPredictionStrategy}
        wheelMode={wheelMode}
        onWheelModeChange={setWheelMode}
      />

      <ShareModal 
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        latestDraw={latestDraw}
        predictions={predictions}
        initialSelection={shareInitialSelection}
      />
    </div>
  );
};

export default App;